import { useMemo } from 'react';

import { useGetApiV1TutoraiUsage } from '@/shared/api/generated';

import type { TutorStudentUsage } from '../types/tutorAi';
import { normalizeStudentUsage } from '../utils/normalizeTutorAi';

/**
 * Janela consultada no relatório de uso de um aluno.
 *
 * Recorta apenas a série diária, o breakdown e o total do período — os limites e as flags
 * de bloqueio que a API devolve são sempre do dia, da semana e do mês correntes,
 * independente do período pedido.
 */
export interface TutorUsagePeriod {
  /** ISO 8601 (YYYY-MM-DD). */
  readonly start: string;
  /** ISO 8601 (YYYY-MM-DD). */
  readonly end: string;
}

/**
 * Relatório de uso e limites de tokens de um aluno no período.
 *
 * É o único endpoint do módulo com série diária, breakdown por provedor/operação e
 * histórico mensal — o dashboard agregado devolve só totais.
 *
 * @param personId - Id da pessoa
 * @param period - Janela consultada
 * @returns Relatório normalizado, estados de carregamento/erro e `refetch` para nova tentativa
 *
 * @example
 * ```tsx
 * const { usage } = useTutorStudentUsage(55, { start: '2026-07-01', end: '2026-07-31' });
 * ```
 */
export function useTutorStudentUsage(personId: number, period: TutorUsagePeriod) {
  const query = useGetApiV1TutoraiUsage(
    { personId, start: period.start, end: period.end },
    // `personId` era param de path, e o cliente gerado embutia `enabled: !!personId` sozinho.
    // Virou query param opcional na migração da API — a guarda implícita desapareceu do
    // cliente e passou a ser responsabilidade deste wrapper. Sem ela, um segmento de URL
    // malformado (`Number(personId)` resultando em `NaN`) dispararia a consulta com
    // `personId=NaN` em vez de simplesmente não disparar.
    { query: { enabled: Number.isFinite(personId) && personId > 0 } },
  );

  const usage: TutorStudentUsage = useMemo(
    () => normalizeStudentUsage(query.data ?? {}),
    [query.data],
  );

  return {
    usage,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  } as const;
}
